import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import api, { BACKEND_URL } from "@/lib/api";
import { getCoverUrl, getPlaylistCoverUrl, getProfilePhotoUrl } from "@/lib/mediaUrls";
import { toast } from "sonner";
import { BadgeWithModal } from "@/components/BadgeWithModal";
import { evolveBadges } from "@/lib/badges";
import GrowthAssistant from "@/components/GrowthAssistant";
import CollabSuggestions from "@/components/CollabSuggestions";
import AIPlaylistBuilderDialog from "@/components/AIPlaylistBuilderDialog";
import HostStatsCard from "@/components/HostStatsCard";
import RgmRadioCard from "@/components/RgmRadioCard";
import BoostPulse from "@/components/BoostPulse";
import DashboardContestTile from "@/components/contests/DashboardContestTile";
import ReviewHistoryModal from "@/components/ReviewHistoryModal";
import PushMilestoneToaster from "@/components/PushMilestoneToaster";
import NowFeaturedShareModal from "@/components/NowFeaturedShareModal";
import SharedNavigation from "@/components/SharedNavigation";
import SellThisTrackButton from "@/components/SellThisTrackButton";
import CommunityPulseWidget from "@/components/CommunityPulseWidget";
import DailyTasksCard from "@/components/DailyTasksCard";

const DashboardPage = () => {
  const [user, setUser] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  // Patch A · Issue 1 (Feb 2026) — Dashboard authoritative count +
  // cursor pagination. `submissionsTotal` is the count returned by
  // the backend `total` field (every submission this artist owns);
  // `hasMoreSubmissions` / `nextSubmissionsCursor` drive Load More.
  const [submissionsTotal, setSubmissionsTotal] = useState(0);
  const [hasMoreSubmissions, setHasMoreSubmissions] = useState(false);
  const [nextSubmissionsCursor, setNextSubmissionsCursor] = useState(null);
  const [loadingMoreSubmissions, setLoadingMoreSubmissions] = useState(false);
  const [purchases, setPurchases] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [referralData, setReferralData] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [requestingVerification, setRequestingVerification] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [followingInProgress, setFollowingInProgress] = useState({});
  const [onboarding, setOnboarding] = useState(null);
  const [onboardingDismissing, setOnboardingDismissing] = useState(false);
  const [reviewHistoryTrack, setReviewHistoryTrack] = useState(null); // {submission_id, track_title}
  // Phase 9.7.20 — Now Featured share modal — passes a full submission row
  const [shareFeatureSubmission, setShareFeatureSubmission] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      fetchSubmissions();
      fetchPurchases(location.state.user.email);
      fetchReferralData();
      fetchPlaylists();
      fetchVerificationStatus();
      fetchSuggestions(location.state.user.user_id);
      fetchOnboarding(location.state.user.user_id);
      // Bug fix (Item 6): early-return path was never clearing the loading
      // state, leaving the dashboard stuck on the spinner forever when
      // navigated to via Link state. Always stop loading here.
      setLoading(false);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await api.get(`/auth/me`);
        setUser(response.data);
        await fetchSubmissions();
        await fetchPurchases(response.data.email);
        await fetchReferralData();
        await fetchPlaylists();
        await fetchVerificationStatus();
        fetchSuggestions(response.data.user_id);
        fetchOnboarding(response.data.user_id);
      } catch (error) {
        console.error('Auth check failed:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [location.state, navigate]);

  const fetchSuggestions = async (userId) => {
    try {
      const response = await api.get(`/suggestions/follow`, {
        params: userId ? { current_user_id: userId, limit: 8 } : { limit: 8 }
      });
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    }
  };

  const handleFollowSuggestion = async (targetUserId) => {
    if (!user?.user_id || followingInProgress[targetUserId]) return;
    setFollowingInProgress(prev => ({ ...prev, [targetUserId]: true }));
    try {
      await api.post(`/artist/${targetUserId}/follow`, { follower_id: user.user_id });
      toast.success('Followed!');
      // Remove from suggestions
      setSuggestions(prev => prev.filter(s => s.user_id !== targetUserId));
    } catch (error) {
      // Phase 15.5.2 — coerce FastAPI 422 detail arrays/objects to a string
      // so toast.error never receives a non-string child that would crash
      // React during the toast render.
      const d = error?.response?.data?.detail;
      let msg = 'Failed to follow';
      if (typeof d === 'string') msg = d;
      else if (Array.isArray(d)) msg = d.map((x) => x?.msg || '').filter(Boolean).join(', ') || msg;
      else if (d && typeof d === 'object') msg = d.msg || msg;
      toast.error(msg);
    } finally {
      setFollowingInProgress(prev => ({ ...prev, [targetUserId]: false }));
    }
  };

  const fetchOnboarding = async (userId) => {
    if (!userId) return;
    try {
      const response = await api.get('/onboarding/status', { params: { current_user_id: userId } });
      setOnboarding(response.data);
    } catch (error) {
      console.error('Onboarding fetch failed:', error);
    }
  };

  const handleDismissOnboarding = async () => {
    if (!user?.user_id) return;
    setOnboardingDismissing(true);
    try {
      await api.post('/onboarding/dismiss', null, { params: { current_user_id: user.user_id } });
      setOnboarding(prev => prev ? { ...prev, dismissed: true } : prev);
    } catch (error) {
      toast.error('Failed to dismiss');
    } finally {
      setOnboardingDismissing(false);
    }
  };

  const handleCopyReferralAndMarkShared = async () => {
    if (!referralData?.referral_link) return;
    try {
      await navigator.clipboard.writeText(referralData.referral_link);
      toast.success('Referral link copied!');
      if (user?.user_id) {
        try {
          await api.post('/onboarding/share-referral', null, { params: { current_user_id: user.user_id } });
          fetchOnboarding(user.user_id);
        } catch (_) { /* non-fatal */ }
      }
    } catch (error) {
      toast.error('Could not copy link');
    }
  };

  const fetchVerificationStatus = async () => {
    try {
      const response = await api.get('/verification/status');
      setVerificationStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch verification status:', error);
    }
  };

  const requestVerification = async () => {
    setRequestingVerification(true);
    try {
      const response = await api.post('/verification/request');
      setVerificationStatus(response.data);
      toast.success("Verification request submitted!");
    } catch (error) {
      console.error('Verification request failed:', error);
      toast.error(error.response?.data?.detail || "Failed to submit verification request");
    } finally {
      setRequestingVerification(false);
    }
  };

  const fetchPlaylists = async () => {
    try {
      const response = await api.get('/playlists/my');
      setPlaylists(response.data.playlists || []);
    } catch (error) {
      console.error('Failed to fetch playlists:', error);
    }
  };

  const fetchReferralData = async () => {
    try {
      const response = await api.get('/referral/my-code');
      setReferralData(response.data);
    } catch (error) {
      console.error('Failed to fetch referral data:', error);
    }
  };

  const fetchSubmissions = async () => {
    try {
      // Patch A · Issue 1 — request page 1 via the new paginated
      // envelope (`/submissions/my?limit=100`). Response shape:
      // {items, total, has_more, next_cursor, limit}. Fall back to
      // the legacy array shape defensively so a partial rollback
      // doesn't blank the Dashboard.
      const response = await api.get(`/submissions/my?limit=100`);
      const data = response.data;
      if (Array.isArray(data)) {
        // legacy shape — should not occur post-Patch-A
        setSubmissions(data);
        setSubmissionsTotal(data.length);
        setHasMoreSubmissions(false);
        setNextSubmissionsCursor(null);
      } else {
        const items = Array.isArray(data?.items) ? data.items : [];
        setSubmissions(items);
        setSubmissionsTotal(
          typeof data?.total === 'number' ? data.total : items.length,
        );
        setHasMoreSubmissions(!!data?.has_more);
        setNextSubmissionsCursor(data?.next_cursor || null);
      }
    } catch (error) {
      console.error('Failed to fetch submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreSubmissions = async () => {
    if (!nextSubmissionsCursor || loadingMoreSubmissions) return;
    setLoadingMoreSubmissions(true);
    try {
      const cursor = encodeURIComponent(nextSubmissionsCursor);
      const response = await api.get(
        `/submissions/my?limit=100&after=${cursor}`,
      );
      const data = response.data || {};
      const items = Array.isArray(data.items) ? data.items : [];
      setSubmissions((prev) => {
        // dedupe by submission_id so a rapid double-click cannot
        // insert a duplicate
        const seen = new Set(prev.map((s) => s.submission_id));
        return [
          ...prev,
          ...items.filter((s) => !seen.has(s.submission_id)),
        ];
      });
      setHasMoreSubmissions(!!data.has_more);
      setNextSubmissionsCursor(data.next_cursor || null);
    } catch (error) {
      console.error('Failed to load more submissions:', error);
    } finally {
      setLoadingMoreSubmissions(false);
    }
  };

  const fetchPurchases = async (email) => {
    if (!email) return;
    try {
      const response = await api.get(`/magazine/my-purchases?user_email=${email}`);
      setPurchases(response.data.purchases || []);
    } catch (error) {
      console.error('Failed to fetch purchases:', error);
    }
  };

  const handleDownloadPDF = async (issueId, issueSlug) => {
    if (!user?.email) {
      toast.error("Please login to download");
      return;
    }

    setDownloading(issueId);
    try {
      const response = await api.get(
        `/magazine/download/${issueId}`,
        {
          params: { user_email: user.email },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${issueSlug}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Download started!");
    } catch (error) {
      toast.error("Download failed", {
        description: error.response?.data?.detail || "Please try again"
      });
    } finally {
      setDownloading(null);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post(`/auth/logout`, {});
      document.cookie = 'session_token=; path=/; max-age=0';
      localStorage.removeItem('session_token');
      toast.success("Logged out successfully");
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      document.cookie = 'session_token=; path=/; max-age=0';
      localStorage.removeItem('session_token');
      navigate('/login');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'submitted': 'text-blue-400',
      'under_review': 'text-yellow-400',
      'shortlisted': 'text-green-400',
      'contacted': 'text-purple-400',
      'featured': 'text-pink-400',
      'rejected': 'text-red-400'
    };
    return colors[status] || 'text-gray-400';
  };

  const handleDeleteTrack = async (submissionId, trackTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${trackTitle}"? This will remove the track from all playlists and cannot be undone.`)) {
      return;
    }
    
    try {
      await api.delete(`/track/${submissionId}`);
      toast.success("Track deleted successfully");
      // Refresh submissions
      fetchSubmissions();
    } catch (error) {
      console.error('Failed to delete track:', error);
      toast.error(error.response?.data?.detail || "Failed to delete track");
    }
  };

  // Get display name - prefer artist_name over Google name
  const getDisplayName = () => {
    return user?.artist_name || user?.name || 'Artist';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          <p className="mt-4 text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* Global navigation — gives mobile drawer + Creator Hub + Wallet chip + Winners + all top-level routes */}
      <SharedNavigation currentUser={user} activePage="dashboard" />

      {/* Dashboard sub-header (account actions) */}
      <div className="border-b border-gray-800 bg-black/80 backdrop-blur-md pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-3 py-3 flex-wrap">
            <div className="min-w-0 flex-1" data-testid="user-info">
              <div className="flex items-center gap-2">
                <p className="text-white font-semibold truncate text-sm sm:text-base">{getDisplayName()}</p>
                <div className="hidden sm:flex items-center gap-1 flex-wrap">
                  {evolveBadges([...(user?.badges || []), ...(user?.referral_badges || [])]).map(badge => (
                    <BadgeWithModal key={badge} type={badge} size="small" />
                  ))}
                </div>
              </div>
              <p className="text-gray-500 text-xs truncate">{user?.email}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {(user?.profile_photo || user?.picture) && (
                <img
                  src={getProfilePhotoUrl(user)}
                  alt={getDisplayName()}
                  className="w-8 h-8 sm:w-9 sm:h-9 rounded-full border-2 border-purple-500/50"
                  data-testid="user-avatar"
                />
              )}
              <Link to="/profile/edit" className="hidden sm:inline-flex">
                <Button variant="outline" size="sm" className="text-white border-gray-700" data-testid="edit-profile-btn">
                  Edit Profile
                </Button>
              </Link>
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="text-white border-gray-700"
                data-testid="logout-button"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Phase 15.6P — Community Pulse Widget (live platform vitals) */}
        <div className="mb-6">
          <CommunityPulseWidget />
          <DailyTasksCard />
        </div>

        {/* iter_193 — 🎧 RGM Radio (permanent listening entry point).
            Placed directly under Community Pulse so it's the first thing
            visitors see after live platform vitals — mobile and desktop. */}
        <div className="mb-8" data-testid="rgm-radio-section">
          <RgmRadioCard />
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-4 mb-8">
          <Link to="/creator-hub" data-testid="dashboard-creator-hub-link">
            <Button className="bg-gradient-to-r from-fuchsia-600 to-purple-600 hover:from-fuchsia-500 hover:to-purple-500 text-white font-bold">
              🎛️ Creator Hub
            </Button>
          </Link>
          <Button
            onClick={() => navigate('/submit')}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold"
            data-testid="new-submission-button"
          >
            Submit New Music
          </Button>
          <Button
            onClick={() => navigate('/submit-beat')}
            className="bg-gradient-to-r from-pink-600 to-fuchsia-600 hover:from-pink-500 hover:to-fuchsia-500 text-white font-bold"
            data-testid="dashboard-upload-beat-btn"
          >
            🎹 Upload Beat
          </Button>
          <Link to={`/artist/${user?.user_id}`}>
            <Button variant="outline" className="text-white border-purple-500/50">
              View Public Profile
            </Button>
          </Link>
          <Link to="/earnings" data-testid="dashboard-earnings-link">
            <Button className="bg-gradient-to-r from-amber-500 to-pink-500 hover:from-amber-400 hover:to-pink-400 text-white font-bold">
              💰 Earnings
            </Button>
          </Link>
          <Link to="/wallet" data-testid="dashboard-wallet-link">
            <Button variant="outline" className="text-amber-200 border-amber-500/50 hover:bg-amber-500/10">
              🪙 Wallet
            </Button>
          </Link>
          <Link to="/leaderboard" data-testid="dashboard-leaderboard-link">
            <Button variant="outline" className="text-amber-200 border-amber-500/50 hover:bg-amber-500/10">
              🏆 Top Gifters
            </Button>
          </Link>
          <Link to="/beams" data-testid="dashboard-live-link">
            <Button className="bg-gradient-to-r from-rose-500 to-pink-500 text-white font-bold">
              🎙️ Live Beams
            </Button>
          </Link>
          {(() => {
            // Phase 9.7.13 — defensive: accept admin role, moderator, A&R role,
            // and founder/admin/ar badges. Matches backend gating.
            const badges = user?.badges || [];
            const isFullAdmin = user?.role === "admin" || user?.role === "moderator"
                                || badges.includes("admin") || badges.includes("founder");
            const isAR = user?.role === "ar" || badges.includes("ar");
            if (!isFullAdmin && !isAR) return null;
            return (
              <>
                <Button
                  onClick={() => navigate('/admin')}
                  className="bg-purple-600 hover:bg-purple-500 text-white"
                  data-testid="admin-dashboard-link"
                >
                  {isFullAdmin ? "Admin Dashboard" : "🎯 A&R Review Queue"}
                </Button>
                {isFullAdmin && (
                  <Button
                    onClick={() => navigate('/admin/media')}
                    variant="outline"
                    className="text-amber-200 border-amber-400/40 hover:bg-amber-500/10"
                    data-testid="dashboard-admin-media-link"
                  >
                    🖼 Artist Media
                  </Button>
                )}
              </>
            );
          })()}
        </div>

        {/* Phase 9.7.12 — Boost Pulse widget. Renders only when the
            current artist has actively-featured/spotlight tracks. */}
        <BoostPulse />

        {/* Phase 21.4 Batch 3D — Contest Center tile. Self-hides when
            the feature flag is off or no active contest exists. */}
        <div className="mb-6" data-testid="dashboard-contest-slot">
          <DashboardContestTile />
        </div>

        {/* Phase 6 quick-access cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="phase6-cards">
          <Link to="/business" data-testid="phase6-business">
            <Card className="p-4 bg-gradient-to-br from-purple-900/30 to-pink-900/20 border-purple-500/30 hover:border-purple-500/60 transition-all h-full">
              <div className="text-3xl mb-1">💼</div>
              <p className="text-white font-bold text-sm">Business Hub</p>
              <p className="text-xs text-gray-400 mt-0.5">List your business + services</p>
            </Card>
          </Link>
          <Link to="/services" data-testid="phase6-services">
            <Card className="p-4 bg-gradient-to-br from-purple-900/30 to-blue-900/20 border-blue-500/30 hover:border-blue-500/60 transition-all h-full">
              <div className="text-3xl mb-1">🛠</div>
              <p className="text-white font-bold text-sm">Services</p>
              <p className="text-xs text-gray-400 mt-0.5">Find or offer services</p>
            </Card>
          </Link>
          <Link to="/courses" data-testid="phase6-courses">
            <Card className="p-4 bg-gradient-to-br from-emerald-900/30 to-green-900/20 border-emerald-500/30 hover:border-emerald-500/60 transition-all h-full">
              <div className="text-3xl mb-1">🎓</div>
              <p className="text-white font-bold text-sm">Courses</p>
              <p className="text-xs text-gray-400 mt-0.5">Workshops & learning</p>
            </Card>
          </Link>
          <Link to="/promote" data-testid="phase6-promote">
            <Card className="p-4 bg-gradient-to-br from-pink-900/30 to-amber-900/20 border-pink-500/30 hover:border-pink-500/60 transition-all h-full">
              <div className="text-3xl mb-1">🚀</div>
              <p className="text-white font-bold text-sm">Promote Track</p>
              <p className="text-xs text-gray-400 mt-0.5">Pro · Boost · Featured</p>
            </Card>
          </Link>
        </div>

        {/* Referral Section - Premium Branded Link Display */}
        {referralData && (
          <div className="mb-8" data-testid="referral-section">
            <Card className="bg-gradient-to-br from-purple-900/40 via-[#1a1a2e] to-pink-900/30 border border-purple-500/40 p-6 shadow-xl shadow-purple-500/10">
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
                      <span className="text-2xl">🎯</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl font-bold text-white">Invite Artists & Earn Badges</h3>
                      <p className="text-purple-400 text-sm font-medium">
                        {referralData.referral_count || 0} artists referred
                      </p>
                    </div>
                    <Link to="/referrals" data-testid="view-all-referrals">
                      <Button variant="outline" size="sm" className="border-purple-500/50 text-purple-200 hover:bg-purple-500/20 whitespace-nowrap">
                        View All →
                      </Button>
                    </Link>
                  </div>
                  
                  {/* Referral Badges Display */}
                  {referralData.referral_badges && referralData.referral_badges.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {referralData.referral_badges.includes('community_builder') && (
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-600 text-white flex items-center gap-1 shadow-md">
                          🏗️ Community Builder
                        </span>
                      )}
                      {referralData.referral_badges.includes('talent_scout') && (
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-600 text-white flex items-center gap-1 shadow-md">
                          🔍 Talent Scout
                        </span>
                      )}
                      {referralData.referral_badges.includes('rgm_ambassador') && (
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-purple-500 to-pink-500 text-white flex items-center gap-1 shadow-md">
                          🌟 RGM Ambassador
                        </span>
                      )}
                      {referralData.referral_badges.includes('ar_connector') && (
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-yellow-500 to-amber-600 text-black flex items-center gap-1 shadow-md">
                          🎯 A&R Connector
                        </span>
                      )}
                    </div>
                  )}
                  
                  {/* Badge Progress */}
                  <div className="text-sm text-gray-400">
                    {referralData.referral_count < 3 && (
                      <span>Next badge: <span className="text-purple-400">Community Builder</span> ({referralData.referral_count}/3)</span>
                    )}
                    {referralData.referral_count >= 3 && referralData.referral_count < 10 && (
                      <span>Next badge: <span className="text-purple-400">Talent Scout</span> ({referralData.referral_count}/10)</span>
                    )}
                    {referralData.referral_count >= 10 && referralData.referral_count < 25 && (
                      <span>Next badge: <span className="text-purple-400">RGM Ambassador</span> ({referralData.referral_count}/25)</span>
                    )}
                    {referralData.referral_count >= 25 && referralData.referral_count < 50 && (
                      <span>Next badge: <span className="text-purple-400">A&R Connector</span> ({referralData.referral_count}/50)</span>
                    )}
                    {referralData.referral_count >= 50 && (
                      <span className="text-yellow-400 font-semibold">🏆 All badges unlocked!</span>
                    )}
                  </div>
                </div>
                
                {/* Branded Referral Link Display */}
                <div className="w-full lg:w-auto">
                  <div className="bg-black/60 border-2 border-purple-500/50 rounded-2xl p-4 shadow-lg shadow-purple-500/10">
                    <p className="text-xs text-gray-400 mb-2 text-center lg:text-left">Your personal invite link</p>
                    
                    {/* Branded Link Display */}
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex-1 bg-gradient-to-r from-purple-900/50 to-pink-900/50 border border-purple-500/30 rounded-xl px-4 py-3 text-center">
                        <p className="text-white font-bold text-lg tracking-wide" data-testid="referral-link-display">
                          rgm.studio/join/<span className="text-purple-400">{referralData.referral_slug}</span>
                        </p>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          navigator.clipboard.writeText(`https://www.rgmstudio.org/join/${referralData.referral_slug}`);
                          toast.success("Referral link copied!");
                        }}
                        className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold shadow-lg shadow-purple-500/25"
                        data-testid="copy-referral-btn"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Copy Link
                      </Button>
                      <Button
                        onClick={() => {
                          const brandedLink = `https://www.rgmstudio.org/join/${referralData.referral_slug}`;
                          if (navigator.share) {
                            navigator.share({
                              title: 'Join RGM Studio',
                              text: `Join me on RGM Studio - the platform where independent artists rise! Use my invite link:`,
                              url: brandedLink
                            });
                          } else {
                            navigator.clipboard.writeText(brandedLink);
                            toast.success("Link copied! Share it with your network.");
                          }
                        }}
                        variant="outline"
                        className="border-purple-500/50 text-purple-300 hover:bg-purple-500/20"
                        data-testid="share-referral-btn"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                        </svg>
                      </Button>
                    </div>
                    
                    <p className="text-xs text-gray-500 mt-3 text-center">
                      Share on socials to grow your network
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Phase 9.4 — 🎙️ Live Hosting Stats */}
        <div className="mb-8" data-testid="host-stats-section">
          <HostStatsCard />
        </div>

        {/* iter_193 — Removed: Featured Performance Recap and Engagement Leaderboard
            (duplicated info shown elsewhere; freed space for RGM Radio discovery).
            RGM Radio itself was moved up directly beneath Community Pulse. */}

        <div className="mb-8">
          <GrowthAssistant />
        </div>
        <div className="mb-8">
          <CollabSuggestions limit={6} />
        </div>

        {/* Onboarding Checklist (Phase 3.1) */}
        {onboarding && !onboarding.dismissed && !onboarding.is_complete && (
          <div className="mb-8" data-testid="onboarding-checklist">
            <Card className="bg-gradient-to-br from-purple-900/40 to-pink-900/20 border border-purple-500/40 p-6">
              <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <span>🚀</span> Get Started on RGM Studio
                  </h3>
                  <p className="text-sm text-gray-400 mt-1">
                    Complete your artist profile to unlock more visibility.
                  </p>
                  <div className="mt-3 flex items-center gap-3">
                    <div className="h-2 bg-black/40 rounded-full overflow-hidden flex-1 max-w-xs">
                      <div
                        className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all"
                        style={{ width: `${onboarding.percent}%` }}
                      ></div>
                    </div>
                    <span className="text-xs font-bold text-purple-300" data-testid="onboarding-progress">
                      {onboarding.completed}/{onboarding.total}
                    </span>
                  </div>
                </div>
                <Button
                  onClick={handleDismissOnboarding}
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 hover:text-white"
                  disabled={onboardingDismissing}
                  data-testid="onboarding-dismiss"
                >
                  Dismiss
                </Button>
              </div>
              <ul className="space-y-2">
                {onboarding.items.map((item) => (
                  <li
                    key={item.key}
                    className={`flex items-center gap-3 p-3 rounded-lg ${item.done ? 'bg-green-500/10 border border-green-500/20' : 'bg-black/20 border border-purple-500/10 hover:border-purple-500/40'} transition-colors`}
                    data-testid={`onboarding-item-${item.key}`}
                  >
                    <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${item.done ? 'bg-green-500 text-black' : 'bg-purple-500/20 text-purple-300 border border-purple-500/40'}`}>
                      {item.done ? '✓' : '○'}
                    </span>
                    <span className={`flex-1 text-sm ${item.done ? 'text-gray-400 line-through' : 'text-white'}`}>
                      {item.label}
                    </span>
                    {!item.done && (
                      item.key === 'share_referral' ? (
                        <Button
                          onClick={handleCopyReferralAndMarkShared}
                          size="sm"
                          variant="outline"
                          className="text-xs border-purple-500/50 text-purple-200 hover:bg-purple-500/20"
                        >
                          Copy link
                        </Button>
                      ) : (
                        <Link to={item.action_href}>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs border-purple-500/50 text-purple-200 hover:bg-purple-500/20"
                          >
                            {item.action_label}
                          </Button>
                        </Link>
                      )
                    )}
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        )}

        {/* Suggested Follows Section */}
        {suggestions.length > 0 && (
          <div className="mb-8" data-testid="suggested-follows-section">
            <Card className="bg-gradient-to-br from-purple-900/30 to-[#1a1a2e] border border-purple-500/30 p-6">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <span>🤝</span> Suggested to Follow
                  </h3>
                  <p className="text-sm text-gray-400 mt-1">Connect with the RGM Studio network</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {suggestions.map((s) => (
                  <div
                    key={s.user_id}
                    className="bg-black/40 border border-purple-500/20 rounded-xl p-4 flex flex-col items-center text-center hover:border-purple-500/50 transition-colors"
                    data-testid={`suggestion-${s.user_id}`}
                  >
                    <Link to={`/artist/${s.user_id}`} className="block">
                      <img
                        src={getProfilePhotoUrl(s)}
                        alt={s.artist_name || s.name}
                        className="w-16 h-16 rounded-full object-cover border-2 border-purple-500/30 mb-3"
                        onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=80&h=80&fit=crop'; }}
                      />
                    </Link>
                    <Link to={`/artist/${s.user_id}`} className="text-white font-semibold text-sm hover:text-purple-300 truncate max-w-full">
                      {s.artist_name || s.name}
                    </Link>
                    <span className="text-xs text-purple-400 mt-1">{s.suggestion_tag}</span>
                    <span className="text-xs text-gray-500 mt-0.5">{s.follower_count} followers</span>
                    <Button
                      size="sm"
                      onClick={() => handleFollowSuggestion(s.user_id)}
                      disabled={!!followingInProgress[s.user_id]}
                      className="mt-3 w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white text-xs"
                      data-testid={`follow-suggestion-${s.user_id}`}
                    >
                      {followingInProgress[s.user_id] ? 'Following…' : '+ Follow'}
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Verification Section */}
        {user && !user.badges?.includes('verified_artist') && (
          <div className="mb-8" data-testid="verification-section">
            <Card className="bg-gradient-to-br from-blue-900/30 to-[#1a1a2e] border border-blue-500/30 p-6">
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
                      <span className="text-2xl">✓</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Get Verified</h3>
                      <p className="text-blue-400 text-sm font-medium">
                        Stand out with a verified badge
                      </p>
                    </div>
                  </div>
                  
                  <p className="text-gray-400 text-sm mb-4">
                    Verified artists receive a blue checkmark, increased visibility, and priority support.
                  </p>
                  
                  {verificationStatus?.status === 'pending' ? (
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                      <p className="text-yellow-400 text-sm flex items-center gap-2">
                        <span>⏳</span>
                        Verification request pending review
                      </p>
                    </div>
                  ) : verificationStatus?.status === 'rejected' ? (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                      <p className="text-red-400 text-sm">
                        Previous request declined. {verificationStatus.reason || 'Please ensure you meet all requirements.'}
                      </p>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">
                      <p className="mb-2">Requirements:</p>
                      <ul className="space-y-1">
                        <li className={`flex items-center gap-2 ${user?.bio ? 'text-green-400' : 'text-gray-500'}`}>
                          <span>{user?.bio ? '✓' : '○'}</span> Complete profile with bio
                        </li>
                        <li className={`flex items-center gap-2 ${user?.profile_photo || user?.picture ? 'text-green-400' : 'text-gray-500'}`}>
                          <span>{user?.profile_photo || user?.picture ? '✓' : '○'}</span> Profile photo uploaded
                        </li>
                        <li className={`flex items-center gap-2 ${submissionsTotal >= 2 ? 'text-green-400' : 'text-gray-500'}`}>
                          <span>{submissionsTotal >= 2 ? '✓' : '○'}</span> At least 2 tracks submitted ({submissionsTotal}/2)
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
                
                <div className="flex-shrink-0">
                  {verificationStatus?.status !== 'pending' && (
                    <Button
                      onClick={requestVerification}
                      disabled={requestingVerification || !user?.bio || submissionsTotal < 2}
                      className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white disabled:opacity-50"
                    >
                      {requestingVerification ? 'Submitting...' : 'Request Verification'}
                    </Button>
                  )}
                  <Link to="/how-to-get-featured" className="block mt-2 text-center text-blue-400 hover:text-blue-300 text-sm">
                    Learn more →
                  </Link>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Submissions */}
        <div>
          <h2 className="text-2xl font-bold text-white mb-6" data-testid="submissions-title">
            Your Submissions ({submissionsTotal})
          </h2>

          {submissionsTotal === 0 ? (
            <Card className="p-8 text-center bg-gradient-to-br from-purple-900/20 to-black border-purple-500/20">
              <p className="text-gray-400 mb-4" data-testid="no-submissions-message">
                You haven't submitted any music yet.
              </p>
              <Button
                onClick={() => navigate('/submit')}
                className="bg-gradient-to-r from-purple-600 to-pink-600"
              >
                Submit Your First Track
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {submissions.map((submission) => (
                <Card
                  key={submission.submission_id}
                  className="overflow-hidden bg-gradient-to-br from-gray-900/80 to-black border-purple-500/20 hover:border-purple-500/50 transition-all"
                  data-testid={`submission-${submission.submission_id}`}
                >
                  <div className="flex">
                    {/* Cover Art - Clickable to Track Page */}
                    <Link to={`/track/${submission.submission_id}`} className="w-32 h-32 flex-shrink-0 relative group">
                      <img
                        src={getCoverUrl(submission) || 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=200&h=200&fit=crop'}
                        alt={submission.track_title}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=200&h=200&fit=crop'; }}
                      />
                      {/* Play Overlay on Hover */}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center">
                          <svg className="w-5 h-5 text-white ml-1" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                          </svg>
                        </div>
                      </div>
                    </Link>
                    
                    {/* Info */}
                    <div className="flex-1 p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-white font-bold text-lg" data-testid="submission-track-title">
                            {submission.track_title || 'Untitled'}
                          </h3>
                          <Link 
                            to={`/artist/${submission.user_id}`} 
                            className="text-purple-400 text-sm hover:text-purple-300 transition-colors"
                            data-testid="submission-artist-link"
                          >
                            {submission.artist_name}
                          </Link>
                          <p className="text-gray-500 text-xs mt-1">{submission.genre}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {submission.status === 'featured' && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-yellow-400 to-amber-500 text-black flex items-center gap-1" data-testid="featured-badge">
                              🎖️ Featured
                            </span>
                          )}
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold uppercase ${getStatusColor(submission.status)} bg-black/50`}>
                            {submission.status.replace('_', ' ')}
                          </span>
                        </div>
                      </div>
                      
                      {/* Score */}
                      {submission.score > 0 && (
                        <div className="mt-3 flex items-center gap-2">
                          <span className="text-gray-400 text-xs">Score:</span>
                          <span className={`font-bold ${submission.score >= 45 ? 'text-green-400' : submission.score >= 30 ? 'text-yellow-400' : 'text-gray-400'}`}>
                            {submission.score}/60
                          </span>
                        </div>
                      )}
                      
                      {/* Actions */}
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Link to={`/track/${submission.submission_id}`}>
                          <Button size="sm" variant="outline" className="text-xs border-purple-500/50 text-purple-300">
                            View Track
                          </Button>
                        </Link>
                        <Link to={`/track/${submission.submission_id}/edit`}>
                          <Button size="sm" variant="outline" className="text-xs border-gray-700 text-gray-400 hover:border-purple-500 hover:text-purple-300">
                            Edit
                          </Button>
                        </Link>
                        {/* iter_226 · Phase 22 — Sell This Track (Singles Store).
                            Hidden unless kill-switch is on OR the viewer is
                            an admin/founder (dark-launch verification). Only
                            renders for the artist who OWNS this submission —
                            enforced both here (currentUser === submission.user_id)
                            and on the backend endpoint. */}
                        {user && user.user_id === submission.user_id && (
                          <SellThisTrackButton submissionId={submission.submission_id} />
                        )}
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-xs border-fuchsia-500/50 text-fuchsia-300 hover:bg-fuchsia-500/10 hover:border-fuchsia-400"
                          onClick={() => setReviewHistoryTrack({ submission_id: submission.submission_id, track_title: submission.track_title })}
                          data-testid={`review-history-btn-${submission.submission_id}`}
                        >
                          📒 Review History
                        </Button>
                        {/* Phase 9.7.20 — Share Your Feature CTA on featured / spotlight rows */}
                        {(submission.status === "featured" || submission.status === "spotlight") && (
                          <Button
                            size="sm"
                            className="text-xs bg-gradient-to-r from-amber-500 to-pink-500 hover:from-amber-400 hover:to-pink-400 text-white font-bold border-0 shadow-md shadow-amber-500/30"
                            onClick={() => setShareFeatureSubmission(submission)}
                            data-testid={`share-feature-btn-${submission.submission_id}`}
                          >
                            🎖️ Share Feature
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-xs border-red-500/50 text-red-400 hover:bg-red-500/20 hover:border-red-500"
                          onClick={() => handleDeleteTrack(submission.submission_id, submission.track_title)}
                          data-testid={`delete-track-${submission.submission_id}`}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Feedback — most-recent A&R note (full history via Review History modal) */}
                  {submission.admin_notes && (
                    <div className="px-4 pb-4">
                      <div className="p-3 bg-gradient-to-br from-fuchsia-900/20 to-purple-900/20 border border-fuchsia-500/30 rounded">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-[11px] uppercase tracking-wider text-fuchsia-300 font-semibold">
                            🎯 Latest A&R Feedback
                          </p>
                          <button
                            onClick={() => setReviewHistoryTrack({ submission_id: submission.submission_id, track_title: submission.track_title })}
                            className="text-[11px] text-purple-300 hover:text-purple-200 underline underline-offset-2"
                            data-testid={`see-all-reviews-${submission.submission_id}`}
                          >
                            See full history →
                          </button>
                        </div>
                        <p className="text-white text-sm whitespace-pre-line">{submission.admin_notes}</p>
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}

          {/* Patch A · Issue 1 — Load More for artists with >100 tracks */}
          {hasMoreSubmissions && (
            <div className="mt-6 flex justify-center">
              <Button
                onClick={loadMoreSubmissions}
                disabled={loadingMoreSubmissions}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:opacity-50"
                data-testid="submissions-load-more-btn"
              >
                {loadingMoreSubmissions
                  ? 'Loading…'
                  : `Load more (${submissionsTotal - submissions.length} remaining)`}
              </Button>
            </div>
          )}
        </div>

        {/* My Playlists Section */}
        <div className="mt-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center gap-2" data-testid="playlists-title">
              <span className="text-xl">🎵</span>
              My Playlists
            </h2>
            <Link to="/playlists">
              <Button variant="outline" size="sm" className="text-purple-400 border-purple-500/50">
                Manage Playlists
              </Button>
            </Link>
            <AIPlaylistBuilderDialog />
          </div>

          {playlists.length === 0 ? (
            <Card className="p-8 text-center bg-gradient-to-br from-purple-900/20 to-black border-purple-500/20">
              <div className="text-4xl mb-4">🎵</div>
              <p className="text-gray-400 mb-4" data-testid="no-playlists-message">
                You haven't created any playlists yet.
              </p>
              <Link to="/playlists">
                <Button className="bg-gradient-to-r from-purple-600 to-pink-600">
                  Create Your First Playlist
                </Button>
              </Link>
            </Card>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {playlists.slice(0, 4).map((playlist) => (
                <Link
                  key={playlist.playlist_id}
                  to={`/playlist/${playlist.playlist_id}`}
                  className="group"
                  data-testid={`playlist-${playlist.playlist_id}`}
                >
                  <Card className="overflow-hidden bg-gradient-to-br from-gray-900/80 to-black border-purple-500/20 hover:border-purple-500/50 transition-all">
                    <div className="aspect-square relative overflow-hidden">
                      <img
                        src={getPlaylistCoverUrl(playlist)}
                        alt={playlist.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1614149162883-504ce4d13909?w=300&h=300&fit=crop'; }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                      
                      {/* Play Button Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center shadow-lg">
                          <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                          </svg>
                        </div>
                      </div>
                      
                      {/* Public/Private Badge */}
                      {playlist.is_public && (
                        <div className="absolute top-2 right-2">
                          <span className="px-2 py-1 bg-green-500/80 text-white text-xs font-bold rounded-full">
                            Public
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <div className="p-3">
                      <h3 className="text-white font-semibold truncate group-hover:text-purple-300 transition-colors">
                        {playlist.name}
                      </h3>
                      <p className="text-gray-500 text-sm">
                        {playlist.track_ids?.length || 0} tracks
                      </p>
                    </div>
                  </Card>
                </Link>
              ))}
              
              {/* Create New Playlist Card */}
              <Link to="/playlists" className="group">
                <Card className="aspect-square flex flex-col items-center justify-center bg-gradient-to-br from-purple-900/20 to-black border-purple-500/20 border-dashed hover:border-purple-500/50 transition-all">
                  <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center mb-3 group-hover:bg-purple-500/30 transition-colors">
                    <svg className="w-6 h-6 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </div>
                  <p className="text-purple-400 font-medium">Create Playlist</p>
                </Card>
              </Link>
            </div>
          )}
        </div>

        {/* My Library - Magazine Purchases */}
        <div className="mt-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white" data-testid="library-title">
              My Library
            </h2>
            <Link to="/magazine">
              <Button variant="outline" size="sm" className="text-purple-400 border-purple-500/50">
                Browse Magazine
              </Button>
            </Link>
          </div>

          {purchases.length === 0 ? (
            <Card className="p-8 text-center bg-gradient-to-br from-purple-900/20 to-black border-purple-500/20">
              <div className="text-4xl mb-4">📚</div>
              <p className="text-gray-400 mb-4" data-testid="no-purchases-message">
                You haven't purchased any magazine issues yet.
              </p>
              <Link to="/magazine">
                <Button className="bg-gradient-to-r from-purple-600 to-pink-600">
                  Browse RGM Magazine
                </Button>
              </Link>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {purchases.map((purchase) => (
                <Card
                  key={purchase.purchase_id}
                  className="overflow-hidden bg-gradient-to-br from-gray-900/80 to-black border-purple-500/20 hover:border-purple-500/50 transition-all"
                  data-testid={`purchase-${purchase.purchase_id}`}
                >
                  <div className="relative">
                    {/* Cover Image */}
                    <Link to={`/magazine/issue/${purchase.issue_slug}`}>
                      <img
                        src={`${BACKEND_URL}/api/magazine/public/cover/${purchase.issue_cover}`}
                        alt={purchase.issue_title}
                        className="w-full h-48 object-cover"
                        onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1614332287897-cdc485fa562d?w=400&h=300&fit=crop'; }}
                      />
                      {/* Owned Badge */}
                      <div className="absolute top-2 right-2 px-2 py-1 bg-green-500/90 rounded-full text-xs font-bold text-white flex items-center gap-1">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Owned
                      </div>
                    </Link>
                  </div>
                  
                  <div className="p-4">
                    <Link to={`/magazine/issue/${purchase.issue_slug}`}>
                      <h3 className="text-white font-bold text-lg hover:text-purple-300 transition-colors" data-testid="purchase-title">
                        {purchase.issue_title}
                      </h3>
                    </Link>
                    <p className="text-gray-500 text-xs mt-1">
                      Purchased {new Date(purchase.purchase_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </p>
                    
                    {/* Actions */}
                    <div className="mt-4 flex gap-2">
                      <Button
                        onClick={() => handleDownloadPDF(purchase.issue_id, purchase.issue_slug)}
                        disabled={downloading === purchase.issue_id}
                        size="sm"
                        className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white text-xs"
                        data-testid={`download-${purchase.issue_id}`}
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        {downloading === purchase.issue_id ? "..." : "PDF"}
                      </Button>
                      <Link to={`/magazine/issue/${purchase.issue_slug}`} className="flex-1">
                        <Button size="sm" variant="outline" className="w-full text-xs border-purple-500/50 text-purple-300">
                          View Issue
                        </Button>
                      </Link>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Phase 9.7.15 — A&R Review History modal */}
      <ReviewHistoryModal
        submissionId={reviewHistoryTrack?.submission_id}
        trackTitle={reviewHistoryTrack?.track_title}
        open={!!reviewHistoryTrack}
        onOpenChange={(o) => { if (!o) setReviewHistoryTrack(null); }}
      />

      {/* Phase 9.7.16 — 🎉 Push Milestone Toaster (background poller).
          Surfaces celebration toasts + confetti when a track crosses a
          push reach threshold (10K / 25K / 50K / 100K / 250K / 500K / 1M). */}
      <PushMilestoneToaster />

      {/* Phase 9.7.20 — Now Featured share modal (artist-side) */}
      <NowFeaturedShareModal
        open={!!shareFeatureSubmission}
        onOpenChange={(o) => { if (!o) setShareFeatureSubmission(null); }}
        submission={shareFeatureSubmission}
        artist={{
          artist_name: shareFeatureSubmission?.artist_display_name || shareFeatureSubmission?.display_artist_name || shareFeatureSubmission?.artist_name,
          profile_photo: shareFeatureSubmission?.user_profile_photo,
          picture: shareFeatureSubmission?.user_picture,
        }}
      />
    </div>
  );
};

export default DashboardPage;
